import { type MessageDescriptor } from 'react-intl';

import SlidesIcon from '@atlaskit/icon-lab/core/ai-generative-slides';
import LiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import DatabaseIcon from '@atlaskit/icon/core/database';
import PageIcon from '@atlaskit/icon/core/page';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import type { IconTileProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';
import { ContentType } from '@atlassian/conversation-assistant-store/controllers/preview-content/contentType';

import { messages } from './messages';

export type PreviewItemIconConfig = {
	icon: IconTileProps['icon'];
	/** IconTile's non-bold appearance — neutral background, colour carried by the icon glyph only. */
	appearance: IconTileProps['appearance'];
	/** IconTile's bold appearance — saturated background (the preview card header's existing look). */
	boldAppearance: IconTileProps['appearance'];
	/** Glyph colour for the bare 'plain' variant (no tile/background at all), matches `appearance`. */
	iconGlyphColor: IconColor;
	altText: MessageDescriptor;
};

/**
 * Single source of truth for the icon, colour and alt text of a preview item,
 * keyed by content type. Every surface that shows a preview item (preview card
 * header, title dropdown, finalize list, chat cards) renders from this config
 * instead of re-deciding the icon per spot.
 *
 * Returns both the bold and non-bold IconTile appearance — see
 * https://atlassian.design/components/icon/icon-tile/examples#appearance — so the caller
 * (PreviewItemIcon) can pick between them based on its own `bold` prop.
 */
export const getPreviewItemIconConfig = (
	contentType: ContentType | null,
): PreviewItemIconConfig | null => {
	switch (contentType) {
		case ContentType.whiteboard:
			return {
				icon: WhiteboardIcon,
				appearance: 'teal',
				boldAppearance: 'tealBold',
				iconGlyphColor: token('color.icon.accent.teal'),
				altText: messages.whiteboardIconAlt,
			};
		case ContentType.liveDoc:
			return {
				icon: LiveDocIcon,
				appearance: 'magenta',
				boldAppearance: 'magentaBold',
				iconGlyphColor: token('color.icon.accent.magenta'),
				altText: messages.liveDocIconAlt,
			};
		case ContentType.database:
			return {
				icon: DatabaseIcon,
				appearance: 'purple',
				boldAppearance: 'purpleBold',
				iconGlyphColor: token('color.icon.accent.purple'),
				altText: messages.databaseIconAlt,
			};
		case ContentType.slides:
			return {
				icon: SlidesIcon,
				appearance: 'green',
				boldAppearance: 'greenBold',
				iconGlyphColor: token('color.icon.accent.green'),
				altText: messages.slidesIconAlt,
			};
		case ContentType.page:
			return {
				icon: PageIcon,
				appearance: 'blue',
				boldAppearance: 'blueBold',
				iconGlyphColor: token('color.icon.accent.blue'),
				altText: messages.pageIconAlt,
			};
		default:
			return null;
	}
};
